// biome-ignore lint/style/useImportType: keeping value import for compatibility
import * as React from 'react';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';

// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { CrehanaTFunction, useTranslation } from '@crehana/i18n';
import Check2 from '@crehana/icons/Check2';
import InfoOutline from '@crehana/icons/InfoOutline';
// eslint-disable-next-line no-restricted-imports
import { greenMain, redMain } from '@crehana/ui/dist/styles/colors';

// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { TKeys } from '../../../../../i18n/types';
import { DEFAULT_INPUT_MAX_LENGTH, LABEL_WARNING_MESSAGE } from './constants';
import { StyledInput } from './styles/StyledInput';
// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { InputMode, InputState, Validation } from './types';
// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { Mask } from './utils';

const generateId = (label: string) =>
  label
    .toLowerCase()
    .normalize('NFD')
    .replace(/\s/g, '-')
    .replace(/[\u0300-\u036f]/g, '');

const defaultValidation =
  (required: boolean, value: string, t: CrehanaTFunction<TKeys>): Validation =>
  () => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (required && value === '')
      return { isValid: false, errorMessage: t('REQUIRED_FIELD') };
    return { isValid: true, errorMessage: '' };
  };

type Props = {
  onChange: (state: InputState) => void;
  validation?: Validation;
  label: string;
  value?: string;
  information?: string;
  hideLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  disabledMessage?: string;
  darkMode?: boolean;
  mask?: Mask;
  maxLength?: number;
  inputMode?: InputMode;
  required?: boolean;
};

const InputField: React.FC<React.PropsWithChildren<Props>> = ({
  onChange,
  validation,
  label,
  placeholder = '',
  value: forcedValue,
  information,
  disabledMessage,
  hideLabel = false,
  disabled = false,
  darkMode = false,
  required = false,
  inputMode = 'text',
  mask,
  maxLength,
}) => {
  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!label) console.warn(LABEL_WARNING_MESSAGE);
  const { t } = useTranslation();
  const [firstInput, setFirstInput] = useState(true);
  const [value, setValue] = useState(forcedValue || '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [cursorPosition, setCursorPosition] = useState<number | null>();

  const input = useRef<HTMLInputElement | null>(null);
  const inputId = generateId(label);
  const hasError = errorMessage !== '' && !firstInput;
  const validate = validation || defaultValidation(required, value, t);

  useEffect(() => {
    if (input.current && cursorPosition) {
      input.current.selectionStart = cursorPosition;
      input.current.selectionEnd = cursorPosition;
    }
  }, [cursorPosition]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = event.currentTarget;
    const { selectionStart } = event.target;

    const { maskedValue, cursorPosition: newCursorPosition } = mask?.format(
      inputValue,
      {
        previousValue: value,
        selectionStart: selectionStart || 0,
      },
    ) || { maskedValue: inputValue, cursorPosition: selectionStart };

    const rawValue = mask?.unformat(maskedValue) || inputValue;
    const validationResult = validate({ rawValue, maskedValue });
    const isValid = validationResult.isValid && !(required && rawValue === '');

    setCursorPosition(newCursorPosition);
    setErrorMessage(validationResult.errorMessage);
    onChange({
      value: rawValue,
      maskedValue,
      isValid,
    });
    setValue(maskedValue);
  };

  const handleBlur = () => {
    setFirstInput(false);

    const cleanValue = value.trim().replace(/\s+/g, ' ');
    const rawValue = mask?.unformat(cleanValue) || cleanValue;
    const maskedValue = cleanValue;
    const { isValid, errorMessage } = validate({
      rawValue,
      maskedValue,
    });

    setErrorMessage(errorMessage);
    setValue(cleanValue);

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (value.length !== cleanValue.length)
      onChange({ isValid, maskedValue: cleanValue, value: rawValue });
  };

  const labelColor = darkMode ? 'text-white' : 'text-base-main';
  const informationColor = darkMode ? 'text-base-lighter' : 'text-gray-dark';
  const inputBackground = darkMode ? 'bg-evelate-dark-03dp' : 'bg-white';

  return (
    <div className="w-full">
      <label
        className={`block mb-8 font-poppins font-semibold text-16 ${labelColor}`}
        htmlFor={inputId}
        hidden={hideLabel}
      >
        {label}
        {required && <span className="text-red-main">*</span>}
      </label>
      {information && (
        <p className={`font-body4 mb-8 ${informationColor}`}>{information}</p>
      )}
      <div className="relative flex items-center">
        <StyledInput
          className={`w-full h-56 border border-solid outline-none rounded-10 py-16 pl-16 pr-40 font-poppins text-14 font-normal ${labelColor} ${inputBackground}`}
          ref={input}
          id={inputId}
          name={inputId}
          type="text"
          placeholder={placeholder}
          hasError={hasError}
          disabled={disabled}
          darkMode={darkMode}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-errormessage="error-message"
          aria-invalid={hasError || disabled}
          value={forcedValue || value}
          maxLength={maxLength || mask?.maxLength || DEFAULT_INPUT_MAX_LENGTH}
          inputMode={inputMode}
        />
        {hasError && (
          <span className="absolute mr-16 right-0">
            <InfoOutline size={24} color={redMain} />
          </span>
        )}
        {!hasError && !firstInput && (
          <span className="absolute mr-16 right-0">
            <Check2 size={24} color={greenMain} />
          </span>
        )}
      </div>
      {disabled && disabledMessage && (
        <p id="error-message" className="mt-8 text-gray-dark font-caption">
          {disabledMessage}
        </p>
      )}
      {hasError && !disabled && (
        <p id="error-message" className="mt-8 text-red-main font-caption">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default InputField;
